import { z } from "zod";

import { AlertTypes } from "@/utils/alert";

import { groupSchema } from "./group";
import { relationshipSchema } from "./relationship";
import { statusSchema } from "./status";
import { dateSchema, emptyArraySchema } from "./utils";

const alertAccount = z.object({
  acct: z.string(),
  avatar: z.string(),
  avatar_static: z.string().url().optional(),
  display_name: z.string(),
  id: z.string(),
  locked: z.boolean(),
  username: z.string(),
  verified: z.boolean(),
  relationship: relationshipSchema.optional().catch(undefined),
});

const baseAlertSchema = z.object({
  accounts: z.array(alertAccount).catch([]),
  alert_at: dateSchema,
  id: z.string(),
  type: z.string(),
  group: groupSchema.nullable().catch(null),
  status: statusSchema.nullable().catch(null),
  total_count: z.number(),
});

const reactionsAlertSchema = baseAlertSchema.extend({
  accounts: z.array(alertAccount).min(1),
  type: z.literal(AlertTypes.REACTIONS),
  status: statusSchema,
  group: z.null(),
});

const followAlertSchema = baseAlertSchema.extend({
  accounts: z.array(alertAccount).min(1),
  type: z.literal(AlertTypes.FOLLOWS),
  status: z.null(),
  group: z.null(),
});

const groupPromotedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.GROUP_PROMOTION),
  group: groupSchema,
  status: z.null(),
});

const groupDemotedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.GROUP_DEMOTION),
  group: groupSchema,
  status: z.null(),
});

const groupRequestAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.GROUP_REQUESTS),
  group: groupSchema,
  status: z.null(),
});

const marketingMessageAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.MARKETING_MESSAGE),
  group: z.null(),
  status: statusSchema,
});

const mentionAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.MENTION),
  group: z.null(),
  status: statusSchema,
});

const pollCloseAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.POLL_CLOSE),
  group: z.null(),
  status: statusSchema,
});

const quoteAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.QUOTE),
  group: z.null(),
  status: statusSchema,
});

const reblogAlertSchema = baseAlertSchema.extend({
  accounts: z.array(alertAccount).min(1),
  type: z.literal(AlertTypes.REBLOGS),
  group: z.null(),
  status: statusSchema,
});

const replyAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.REPLY),
  group: z.null(),
  status: statusSchema,
});

const subscribedStatusAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.SUBSCRIBED_STATUS),
  group: z.null(),
  status: statusSchema,
});

const tvReminderAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.TV_REMINDER),
  group: z.null(),
  status: statusSchema,
});

// TMT-192 boost lifecycle alerts. Submitted/approved always carry the boosted
// status (the API drops them when it is gone); the terminal-state types keep
// rendering after the status is deleted, so status is nullable there.
const boostSubmittedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.BOOST_SUBMITTED),
  group: z.null(),
  status: statusSchema,
});

const boostApprovedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.BOOST_APPROVED),
  group: z.null(),
  status: statusSchema,
});

const boostRejectedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.BOOST_REJECTED),
  group: z.null(),
  status: statusSchema.nullable().catch(null),
});

const boostCompletedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.BOOST_COMPLETED),
  group: z.null(),
  status: statusSchema.nullable().catch(null),
});

const boostCancelledAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.BOOST_CANCELLED),
  group: z.null(),
  status: statusSchema.nullable().catch(null),
});

const boostPaymentFailedAlertSchema = baseAlertSchema.extend({
  accounts: emptyArraySchema,
  type: z.literal(AlertTypes.BOOST_PAYMENT_FAILED),
  group: z.null(),
  status: statusSchema.nullable().catch(null),
});

const alertWithStatus = baseAlertSchema.extend({
  status: statusSchema,
});

const alertSchema = z.discriminatedUnion("type", [
  reactionsAlertSchema,
  followAlertSchema,
  groupPromotedAlertSchema,
  groupDemotedAlertSchema,
  groupRequestAlertSchema,
  marketingMessageAlertSchema,
  mentionAlertSchema,
  pollCloseAlertSchema,
  quoteAlertSchema,
  reblogAlertSchema,
  replyAlertSchema,
  subscribedStatusAlertSchema,
  tvReminderAlertSchema,
  boostSubmittedAlertSchema,
  boostApprovedAlertSchema,
  boostRejectedAlertSchema,
  boostCompletedAlertSchema,
  boostCancelledAlertSchema,
  boostPaymentFailedAlertSchema,
]);

type Alert = z.infer<typeof alertSchema>;
type ReactionAlert = z.infer<typeof reactionsAlertSchema>;
type FollowAlert = z.infer<typeof followAlertSchema>;
type GroupPromotedAlert = z.infer<typeof groupPromotedAlertSchema>;
type GroupDemotedAlert = z.infer<typeof groupDemotedAlertSchema>;
type GroupRequestAlert = z.infer<typeof groupRequestAlertSchema>;
type MarketingMessageAlert = z.infer<typeof marketingMessageAlertSchema>;
type MentionAlert = z.infer<typeof mentionAlertSchema>;
type PollCloseAlert = z.infer<typeof pollCloseAlertSchema>;
type QuoteAlert = z.infer<typeof quoteAlertSchema>;
type ReblogAlert = z.infer<typeof reblogAlertSchema>;
type ReplyAlert = z.infer<typeof replyAlertSchema>;
type SubscribedStatusAlert = z.infer<typeof subscribedStatusAlertSchema>;
type TvReminderAlert = z.infer<typeof tvReminderAlertSchema>;
type BoostSubmittedAlert = z.infer<typeof boostSubmittedAlertSchema>;
type BoostApprovedAlert = z.infer<typeof boostApprovedAlertSchema>;
type BoostRejectedAlert = z.infer<typeof boostRejectedAlertSchema>;
type BoostCompletedAlert = z.infer<typeof boostCompletedAlertSchema>;
type BoostCancelledAlert = z.infer<typeof boostCancelledAlertSchema>;
type BoostPaymentFailedAlert = z.infer<typeof boostPaymentFailedAlertSchema>;
type AlertWithStatus = z.infer<typeof alertWithStatus>;
type AlertAccount = z.infer<typeof alertAccount>;

export {
  alertSchema,
  alertAccount as alertAccountSchema,
  reactionsAlertSchema,
  type Alert,
  type ReactionAlert,
  type FollowAlert,
  type GroupPromotedAlert,
  type GroupDemotedAlert,
  type GroupRequestAlert,
  type MarketingMessageAlert,
  type MentionAlert,
  type PollCloseAlert,
  type QuoteAlert,
  type ReblogAlert,
  type ReplyAlert,
  type SubscribedStatusAlert,
  type TvReminderAlert,
  type BoostSubmittedAlert,
  type BoostApprovedAlert,
  type BoostRejectedAlert,
  type BoostCompletedAlert,
  type BoostCancelledAlert,
  type BoostPaymentFailedAlert,
  type AlertWithStatus,
  type AlertAccount,
};
